import { useState, useContext, useRef, useLayoutEffect } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import Context from '../../../context';
import './index.scss';
import useKeypress from "../../../hooks/useKeypress";
import { buildDocPathEnd, getBackPath } from "../../../helpers";
import ListTitle from "../ListTitle";
import { getEwaBackPath } from "../../../libraries/ewa/helpers";
import { BACK_NAVIGATION_TYPE } from "../../../helpers/constants";
import Spinner from "../Spinner";
import Modal from "../Modal";
import MyDocumentsUploadPanel from "./MyDocumentsUploadPanel";
import MyDocumentControls from "./MyDocumentControls";
import MyDocumentManager from "./MyDocumentManager";
import { useDocumentUpload } from "./useDocumentUpload";

const DocumentList = ({section, folderId, data, showTitle, docScreenName, fileInputRef, getFiles, cancelUploadSignal, setCancelUploadSignal, isUploadOpen, setIsUploadOpen}) => {
  const [backButtonPath, setBackButtonPath] = useState(-1);

  const { viewMode, customHistory, itin } = useContext(Context);
  const navigate = useNavigate();
  const location = useLocation();
  const backButton = useRef(null);

  const {
    isRetry,
    uploadDocConfig,
    setUploadDocConfig,
    myDocsUploadDetails,
    setMyDocsUploadDetails,
    onFileSelect,
    onCancel,
    handleFileUpload,
    isConverting,
  } = useDocumentUpload({ folderId, fileInputRef, cancelUploadSignal, setCancelUploadSignal, setIsUploadOpen });

  useLayoutEffect(() => {
    const defaultBackPath = getBackPath(location.pathname, customHistory);

    const ewaBackPath = getEwaBackPath();
    const backPath = (ewaBackPath?.returnTo && location.pathname === ewaBackPath?.entryPath) ? BACK_NAVIGATION_TYPE : defaultBackPath;
    setBackButtonPath(backPath);
  }, [customHistory, location.pathname]);

  const onKeyPressed = (doc, event) => {
    if (event.key === 'Enter') {
      if (doc?.type !== 'remote') {
        navigate(`${location.pathname}/${doc.type === 'folder' ? doc.id : `view/${doc.id}`}`);
      } else if (doc.remoteUrl) window.open(doc.remoteUrl, '_blank');
    }
  };

  const handleSpanClick = (doc, event) => {
    event.preventDefault();
    const pathEnd = buildDocPathEnd(doc, itin.documents);
    navigate(`${location.pathname}/${pathEnd}`);
  };

  useKeypress('Escape', backButton, () => {
    if (!backButton.current) return
    backButton.current.click();
    navigate(backButton.current)
  });

  // Show mobile screen when uploading traveller document
  if (viewMode !== 'desktop' && isUploadOpen) {
    return (
      <MyDocumentManager
        document={null}
        uploadDocConfig={uploadDocConfig}
        setUploadDocConfig={setUploadDocConfig}
        handleFileUpload={handleFileUpload}
        myDocsUploadDetails={myDocsUploadDetails}
      />
    );
  }

  return (
    <div className={`document-list document-list-${viewMode}`}>
      {showTitle && viewMode === 'desktop' &&
        <ListTitle backButton={backButton} backButtonPath={backButtonPath} docScreenName={docScreenName} />}
      <ul>
        {
          data.map((x, i) => (
            <li
              key={`${section}-document-${i}`}
              tabIndex={0}
              onKeyDown={(event) => onKeyPressed(x, event, i)}
              className={!x.id ? "disabled" : ""}
            >
              {x?.type !== 'remote' ? (
                <div
                  onClick={(e) => handleSpanClick(x, e, i)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="name">{x?.name}</span>
                  {x?.type === 'file' && x?.isUserData &&
                    <MyDocumentControls
                      docId={x.id}
                      restrictedToTravellerIds={x?.restrictedToTravellerIds}
                      isEditable={x.isEditable}
                    />}
                  {x?.type === 'folder' && <img src="/icons/chevron-down.png" alt="" />}
                </div>
              ) : (
                <a
                  href={x?.remoteUrl}
                  target="_blank"
                  rel="noreferrer"
                  tabIndex={-1}
                >
                  <span className="name">{x.name}</span>
                </a>
              )}
            </li>
          ))
        }
      </ul>
      {section === 'my-documents' && (
        <MyDocumentsUploadPanel
          fileInputRef={fileInputRef}
          getFiles={getFiles}
          setMyDocsUploadDetails={setMyDocsUploadDetails}
          folderId={folderId}
          onFileSelect={onFileSelect}
          isUploadOpen={isUploadOpen}
          onCancel={onCancel}
          handleFileUpload={handleFileUpload}
          isRetry={isRetry}
          uploadDocConfig={uploadDocConfig}
          setUploadDocConfig={setUploadDocConfig}
          showUploadHint={data.length === 0}
        />
      )}

      {(myDocsUploadDetails.uploading || isConverting) &&
        <Modal isOpen={myDocsUploadDetails.uploading || isConverting}>
          <div className="center-spinner">
            {myDocsUploadDetails.uploading && <span className="spinner-label">{`${myDocsUploadDetails.percent}%`}</span>}
            <Spinner />
          </div>
        </Modal>
      }
    </div>
  );
};

export default DocumentList;